import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Button, Typography } from '@material-ui/core'

import {
  ConfirmationDialog,
  MenuButton,
  PermittedByAssignmentRole,
} from 'components'
import { AssumeRiskDialog } from './components'

import helpers from 'helpers'
import useSnackbar from 'hooks/useSnackbar'

import theme from 'theme'
import * as service from 'service'
import constants from 'constants/index'

// Ações de risco do controle reprovado em projeto com Revisão: assumir e desfazer.
const RiskAssumptionMenu = ({
  activity,
  governanceProjectId,
  activityCode,
  assignmentRole,
  refresh,
}) => {
  const { INACTIVE_STATUS_ID, MANAGER_ROLE, PROJECT_RESPONSIBLE_ROLE } =
    constants.governanceProject
  const { REJECTED_STATUS_ID, RISK_ASSUMED_STATUS_ID } =
    constants.governanceProjectActivity

  const [loading, setLoading] = useState(false)
  const [isAssumeRiskDialogOpen, setIsAssumeRiskDialogOpen] = useState(false)
  const [isUndoRiskDialogOpen, setIsUndoRiskDialogOpen] = useState(false)

  const snackbar = useSnackbar()

  const isLockedProject =
    activity?.governanceProject?.status === INACTIVE_STATUS_ID ||
    helpers.governanceProject.isProjectLocked(
      activity?.governanceProject?.status,
    )
  const canAssumeRisk =
    activity?.status === REJECTED_STATUS_ID &&
    activity?.latestReview?.status === REJECTED_STATUS_ID
  const canUndoRisk = activity?.status === RISK_ASSUMED_STATUS_ID

  if (
    !activity?.coveredByReview ||
    isLockedProject ||
    (!canAssumeRisk && !canUndoRisk)
  )
    return null

  const trackEvent = (eventId) =>
    helpers.mixpanel.track('Controle de governança', eventId, {
      governanceProjectId,
      activityId: activity?.id,
    })

  const runRequest = async ({
    sendRequest,
    trackingEventId,
    successMessage,
    setDialogOpen,
  }) => {
    try {
      setLoading(true)
      trackEvent(trackingEventId)

      await sendRequest()

      setDialogOpen(false)
      snackbar.open({ message: successMessage, variant: 'success' })
      refresh()
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(error),
        variant: 'error',
      })

      // Conflito (ex.: o controle mudou de estado): recarrega o controle no estado novo.
      if (error?.response?.status === constants.errors.HTTP_CONFLICT_STATUS)
        refresh()
    } finally {
      setLoading(false)
    }
  }

  const handleOpenAssumeRiskDialog = () => {
    trackEvent('enviar-risco-controle')
    setIsAssumeRiskDialogOpen(true)
  }

  const handleOpenUndoRiskDialog = () => {
    trackEvent('desfazer-risco-controle')
    setIsUndoRiskDialogOpen(true)
  }

  const handleAssumeRisk = (justification) =>
    runRequest({
      sendRequest: () =>
        service.dponet.governanceProjectActivity.assumeRisk({
          governanceProjectId,
          id: activity?.id,
          justification,
        }),
      trackingEventId: 'confirmar-enviar-risco-controle',
      successMessage: 'Risco assumido com sucesso!',
      setDialogOpen: setIsAssumeRiskDialogOpen,
    })

  const handleUndoRisk = () =>
    runRequest({
      sendRequest: () =>
        service.dponet.governanceProjectActivity.undoRiskAssumption({
          governanceProjectId,
          id: activity?.id,
        }),
      trackingEventId: 'confirmar-desfazer-risco-controle',
      successMessage: 'Risco assumido desfeito com sucesso!',
      setDialogOpen: setIsUndoRiskDialogOpen,
    })

  return (
    <PermittedByAssignmentRole
      permittedRoles={[MANAGER_ROLE, PROJECT_RESPONSIBLE_ROLE]}
      userRole={assignmentRole}
    >
      <MenuButton id="risk-assumption-menu">
        {canAssumeRisk ? (
          <Button
            color="secondary"
            fullWidth
            size="small"
            onClick={handleOpenAssumeRiskDialog}
          >
            Assumir o risco
          </Button>
        ) : (
          <Button
            color="secondary"
            fullWidth
            size="small"
            onClick={handleOpenUndoRiskDialog}
          >
            Desfazer risco assumido
          </Button>
        )}
      </MenuButton>
      <AssumeRiskDialog
        open={isAssumeRiskDialogOpen}
        setOpen={setIsAssumeRiskDialogOpen}
        activityCode={activityCode}
        loading={loading}
        onAssumeRisk={handleAssumeRisk}
      />
      <ConfirmationDialog
        open={isUndoRiskDialogOpen}
        setOpen={setIsUndoRiskDialogOpen}
        actionAcceptButton={handleUndoRisk}
        loading={loading}
        hiddenIcon
        textButton="Desfazer"
        textButtonCancel="Cancelar"
        text={
          <Box display="flex" flexDirection="column" gridGap={theme.spacing(2)}>
            <Typography variant="h3">Desfazer risco assumido</Typography>
            <Typography>
              {`O controle ${activityCode} voltará a ficar reprovado e a justificativa do risco será descartada. Depois disso, ele pode ser reaberto para correção ou ter o risco assumido novamente.`}
            </Typography>
          </Box>
        }
      />
    </PermittedByAssignmentRole>
  )
}

RiskAssumptionMenu.propTypes = {
  activity: PropTypes.object,
  governanceProjectId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  activityCode: PropTypes.string,
  assignmentRole: PropTypes.string,
  refresh: PropTypes.func.isRequired,
}

export default RiskAssumptionMenu
